<template>
  <div class="admin-panel task-page task-page--email-blast">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Email Blast', to: { name: 'Admin-Email-Blast', params: { lang: $route.params.lang } } }, { label: 'Monthly Deadlines' }]"
      :back-to="{ name: 'Admin-Email-Blast', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in">

      <!-- Hero with current month display -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'calendar-alt']" />
        </div>
        <div>
          <h1 class="tp-hero-title">
            Monthly Deadlines
            <span v-if="serverTime.month_name" class="eb-month-badge">{{ serverTime.month_name }} {{ serverTime.year }}</span>
          </h1>
          <p class="tp-hero-sub">Send deadline reminders to all clients with upcoming deadlines this month. Deadlines are combined from Tax and Cosec / Business Support services.</p>
        </div>
      </div>

      <!-- Summary strip -->
      <div v-if="!loading && tableRows.length" class="tp-summary">
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ tableRows.length }}</span>
          <span class="tp-summary-label">Total Clients</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ eligibleCount }}</span>
          <span class="tp-summary-label">Eligible to Send</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ noDeadlinesCount }}</span>
          <span class="tp-summary-label">No Deadlines</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ noRecipientCount }}</span>
          <span class="tp-summary-label">No Recipient</span>
        </div>
      </div>

      <!-- Table panel -->
      <div class="tp-panel">
        <div class="tp-panel-head">
          <h3>Client Deadline Recipients — {{ serverTime.month_name || '…' }} {{ serverTime.year || '' }}</h3>
          <div class="tp-panel-head-right">
            <app-badge v-if="!loading" size="xl" :caps="false" variant="custom" custom-color="#333739" custom-bg="#F2F3F3" :inner-shadow="true">
              <span>{{ tableRows.length }} client{{ tableRows.length !== 1 ? 's' : '' }}</span>
            </app-badge>
            <app-button
              size="sm"
              variant="ghost"
              :icon="['fas', 'eye']"
              :disabled="loading || !tableRows.length"
              @click="openTemplatePreview"
            >Preview Template</app-button>
            <app-button
              size="sm"
              variant="custom"
              custom-bg="#2563eb"
              custom-hover-bg="#1E40AF"
              :icon="['fas', blasting.done ? 'check' : 'paper-plane']"
              :disabled="loading || blasting.done || !eligibleCount"
              @click="showBlastConfirm = true"
            >{{ blasting.done ? 'Blast Complete' : 'Blast All' }}</app-button>
          </div>
        </div>

        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" spin class="tp-empty-icon" />
          <p>Loading clients…</p>
        </div>

        <div v-else-if="!tableRows.length" class="tp-empty">
          <fa-icon :icon="['fas', 'envelope']" class="tp-empty-icon" />
          <p>No clients found with tax or business support services.</p>
        </div>

        <div v-else class="tp-notify-table-wrap">
          <table class="tp-notify-table eb-blast-table">
            <thead>
              <tr>
                <th class="tp-notify-num">#</th>
                <th class="tp-notify-company">Client</th>
                <th class="eb-th-initial">Initial</th>
                <th class="eb-th-type">Type</th>
                <th class="eb-th-service">Service</th>
                <th class="eb-th-deadlines">Deadlines</th>
                <th class="tp-notify-recipients">Recipients (To)</th>
                <th class="tp-notify-recipients">Recipients (CC)</th>
                <th class="tp-notify-recipients">Recipients (BCC)</th>
                <th class="eb-th-status">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in tableRows" :key="row.id">
                <td class="tp-notify-num">{{ i + 1 }}</td>
                <td class="tp-notify-company">{{ row.client }}</td>
                <td class="eb-td-meta">{{ row.initial || '—' }}</td>
                <td class="eb-td-meta">{{ formatClientType(row.client_type) }}</td>
                <td class="eb-td-service">
                  <div class="tp-svc-tags">
                    <app-badge
                      v-for="svc in row.services"
                      :key="svc.name"
                      :caps="true"
                      size="sm"
                      :services="svc.name"
                    >{{ svc.display_name }}</app-badge>
                  </div>
                </td>
                <td class="eb-td-deadlines">
                  <template v-if="row.deadlines.length">
                    <div v-for="dl in row.deadlines" :key="dl.name" class="eb-deadline-chip">
                      <span class="eb-deadline-name">{{ dl.display_name }}</span>
                      <span v-if="dl.date && dl.date.day" class="eb-deadline-day">{{ dl.date.day }}</span>
                    </div>
                  </template>
                  <span v-else class="tp-notify-no-recipient">—</span>
                </td>
                <td class="tp-notify-recipients eb-recipients">
                  <template v-if="row.to.emails.length || row.to.missing.length">
                    <div v-for="email in row.to.emails" :key="email" class="tp-notify-email" :class="emailClass(row, email)">{{ email }}</div>
                    <div v-for="name in row.to.missing" :key="'mto-' + name" class="tp-notify-email tp-notify-email--missing">
                      <fa-icon :icon="['fas', 'exclamation-triangle']" />
                      No email: {{ name }}
                    </div>
                  </template>
                  <span v-else class="tp-notify-no-recipient">—</span>
                </td>
                <td class="tp-notify-recipients eb-recipients">
                  <template v-if="row.cc.emails.length || row.cc.missing.length">
                    <div v-for="email in row.cc.emails" :key="email" class="tp-notify-email" :class="emailClass(row, email)">{{ email }}</div>
                    <div v-for="name in row.cc.missing" :key="'mcc-' + name" class="tp-notify-email tp-notify-email--missing">
                      <fa-icon :icon="['fas', 'exclamation-triangle']" />
                      No email: {{ name }}
                    </div>
                  </template>
                  <span v-else class="tp-notify-no-recipient">—</span>
                </td>
                <td class="tp-notify-recipients eb-recipients">
                  <template v-if="row.bcc.emails.length || row.bcc.missing.length">
                    <div v-for="email in row.bcc.emails" :key="email" class="tp-notify-email" :class="emailClass(row, email)">{{ email }}</div>
                    <div v-for="name in row.bcc.missing" :key="'mbcc-' + name" class="tp-notify-email tp-notify-email--missing">
                      <fa-icon :icon="['fas', 'exclamation-triangle']" />
                      No email: {{ name }}
                    </div>
                  </template>
                  <span v-else class="tp-notify-no-recipient">—</span>
                </td>
                <td class="eb-td-status">
                  <span class="tp-notify-badge" :class="blastBadgeClass(row)">
                    <fa-icon v-if="row.status === 'sending'" :icon="['fas', 'spinner']" spin />
                    <fa-icon v-else-if="row.status === 'sent'" :icon="['fas', 'check']" />
                    <fa-icon v-else-if="row.status === 'failed'" :icon="['fas', 'times']" />
                    <fa-icon v-else-if="row.status === 'no-deadlines' || row.status === 'no-recipient'" :icon="['fas', 'minus']" />
                    {{ blastStatusLabel(row) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </main>

    <!-- Blast progress overlay — blocks all interaction while sending -->
    <div v-if="blasting.active" class="eb-blast-overlay">
      <div class="eb-blast-card">
        <fa-icon :icon="['fas', 'paper-plane']" class="eb-blast-plane" spin />
        <div class="eb-blast-card-title">Email Blast in Progress</div>
        <div class="eb-blast-card-sub">Please do not close or navigate away from this tab.</div>
        <div class="eb-blast-counts">
          <div class="eb-blast-count eb-blast-count--sent">
            <span class="eb-blast-count-val">{{ sentCount }}</span>
            <span class="eb-blast-count-label">Sent</span>
          </div>
          <div class="eb-blast-count eb-blast-count--skip">
            <span class="eb-blast-count-val">{{ skippedCount }}</span>
            <span class="eb-blast-count-label">Skipped</span>
          </div>
          <div class="eb-blast-count eb-blast-count--fail">
            <span class="eb-blast-count-val">{{ failedCount }}</span>
            <span class="eb-blast-count-label">Failed</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary modal -->
    <div v-if="summary.show" class="tp-notify-overlay" @click.self="summary.show = false">
      <div class="tp-notify-modal eb-summary-modal">
        <div class="tp-notify-head">
          <div class="tp-notify-head-title">
            <fa-icon :icon="['fas', 'paper-plane']" />
            Email Blast Summary
          </div>
          <div class="tp-notify-head-sub">{{ serverTime.month_name }} {{ serverTime.year }} — Blast complete</div>
          <button class="tp-notify-close" @click="summary.show = false">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="tp-notify-body eb-summary-body">
          <div class="eb-summary-grid" :class="{ 'eb-summary-grid--4': summary.partial > 0 }">
            <div class="eb-summary-stat eb-summary-stat--sent">
              <span class="eb-summary-val">{{ summary.sent }}</span>
              <span class="eb-summary-label">Sent</span>
            </div>
            <div v-if="summary.partial > 0" class="eb-summary-stat eb-summary-stat--partial">
              <span class="eb-summary-val">{{ summary.partial }}</span>
              <span class="eb-summary-label">Partial</span>
            </div>
            <div class="eb-summary-stat eb-summary-stat--skip">
              <span class="eb-summary-val">{{ summary.skippedNoDeadlines + summary.skippedNoRecipient }}</span>
              <span class="eb-summary-label">Skipped</span>
            </div>
            <div class="eb-summary-stat eb-summary-stat--fail">
              <span class="eb-summary-val">{{ summary.failed }}</span>
              <span class="eb-summary-label">Failed</span>
            </div>
          </div>

          <div v-if="summary.skippedNoDeadlines" class="eb-summary-note">
            <fa-icon :icon="['fas', 'calendar-times']" />
            {{ summary.skippedNoDeadlines }} client{{ summary.skippedNoDeadlines !== 1 ? 's' : '' }} had no deadlines in {{ serverTime.month_name }}.
          </div>
          <div v-if="summary.skippedNoRecipient" class="eb-summary-note">
            <fa-icon :icon="['fas', 'user-slash']" />
            {{ summary.skippedNoRecipient }} client{{ summary.skippedNoRecipient !== 1 ? 's' : '' }} had no configured recipients (To).
          </div>

          <div v-if="summary.errors.length" class="eb-summary-errors">
            <div class="eb-summary-errors-title">Failed Sends</div>
            <div v-for="e in summary.errors" :key="e.client" class="eb-summary-error-row">
              <span class="eb-summary-error-client">{{ e.client }}</span>
              <span class="eb-summary-error-msg">{{ e.error }}</span>
            </div>
          </div>
        </div>

        <div class="tp-notify-foot">
          <button class="tp-notify-send-btn tp-notify-send-btn--done" @click="summary.show = false">
            <fa-icon :icon="['fas', 'check']" />
            Done
          </button>
        </div>
      </div>
    </div>

    <!-- Blast Confirmation Modal -->
    <div v-if="showBlastConfirm" class="tp-notify-overlay" @click.self="showBlastConfirm = false">
      <div class="tp-notify-modal eb-confirm-modal">

        <div class="tp-notify-head">
          <div class="tp-notify-head-title">
            <fa-icon :icon="['fas', 'paper-plane']" />
            Confirm Email Blast
          </div>
          <div class="tp-notify-head-sub">
            {{ eligibleCount }} email{{ eligibleCount !== 1 ? 's' : '' }} will be sent — {{ serverTime.month_name }} {{ serverTime.year }}
          </div>
          <button class="tp-notify-close" @click="showBlastConfirm = false">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="tp-notify-body eb-confirm-body">
          <!-- Send / skip counts -->
          <div class="eb-confirm-stats">
            <div class="eb-confirm-stat eb-confirm-stat--send">
              <span class="eb-confirm-stat-val">{{ eligibleCount }}</span>
              <span class="eb-confirm-stat-label">Will Send</span>
            </div>
            <div class="eb-confirm-stat eb-confirm-stat--skip">
              <span class="eb-confirm-stat-val">{{ noDeadlinesCount + noRecipientCount }}</span>
              <span class="eb-confirm-stat-label">Will Skip</span>
            </div>
          </div>
          <p class="eb-confirm-note">
            Each eligible client will receive a personalised deadline reminder email.
            Review the exact template before proceeding.
          </p>
        </div>

        <div class="tp-notify-foot">
          <button class="tp-notify-send-btn eb-confirm-preview-btn" @click="openTemplatePreview">
            <fa-icon :icon="['fas', 'eye']" />
            Preview Template
          </button>
          <button class="tp-notify-send-btn eb-confirm-proceed" @click="confirmBlast">
            <fa-icon :icon="['fas', 'paper-plane']" />
            Blast All ({{ eligibleCount }})
          </button>
        </div>

      </div>
    </div>

    <!-- Email Template Preview Modal -->
    <div v-if="showTemplatePreview" class="tp-notify-overlay" @click.self="showTemplatePreview = false">
      <div class="tp-notify-modal eb-tpl-modal">

        <div class="tp-notify-head">
          <div class="tp-notify-head-title">
            <fa-icon :icon="['fas', 'envelope-open-text']" />
            Email Template Preview
          </div>
          <div v-if="previewRow" class="tp-notify-head-sub">
            Sample client: <strong>{{ previewRow.client }}</strong>
          </div>
          <button class="tp-notify-close" @click="showTemplatePreview = false">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div v-if="previewRow" class="tp-notify-body eb-tpl-body">

          <!-- Email envelope headers -->
          <div class="eb-tpl-headers">
            <div class="eb-tpl-header-row">
              <span class="eb-tpl-hkey">Subject</span>
              <span class="eb-tpl-hval">Monthly Deadline Reminder – {{ serverTime.month_name }} {{ serverTime.year }}</span>
            </div>
            <div class="eb-tpl-header-row">
              <span class="eb-tpl-hkey">From</span>
              <span class="eb-tpl-hval">TYNG &amp; CO &lt;management@tyng-ca.com.my&gt;</span>
            </div>
            <div class="eb-tpl-header-row">
              <span class="eb-tpl-hkey">To</span>
              <span class="eb-tpl-hval">
                <template v-if="previewRow.to.emails.length">{{ previewRow.to.emails.join(', ') }}</template>
                <em v-else class="eb-tpl-muted">No recipients configured</em>
              </span>
            </div>
            <div v-if="previewRow.cc.emails.length" class="eb-tpl-header-row">
              <span class="eb-tpl-hkey">CC</span>
              <span class="eb-tpl-hval">{{ previewRow.cc.emails.join(', ') }}</span>
            </div>
            <div v-if="previewRow.bcc.emails.length" class="eb-tpl-header-row">
              <span class="eb-tpl-hkey">BCC</span>
              <span class="eb-tpl-hval">{{ previewRow.bcc.emails.join(', ') }}</span>
            </div>
          </div>

          <!-- Plain-text email body — mirrors the PHP template exactly -->
          <div class="eb-tpl-email-body">
            <p>Dear {{ previewRow.toName }},</p>
            <p>This is a gentle reminder that the following deadline(s) are due in <strong>{{ serverTime.month_name }} {{ serverTime.year }}</strong> for <strong>{{ previewRow.client }}</strong>:</p>
            <div class="eb-tpl-deadlines">
              <div v-for="dl in previewRow.deadlines" :key="dl.name || dl.display_name" class="eb-tpl-dl-row">
                <span class="eb-tpl-dl-bullet">•</span>
                <span class="eb-tpl-dl-name">{{ dl.display_name }}</span>
                <span class="eb-tpl-dl-due">Due: {{ formatDeadlineDate(dl) }}</span>
              </div>
            </div>
            <p>Please ensure all required documents and filings are prepared and submitted before the due date.</p>
            <p>Should you have any questions or require assistance, please do not hesitate to reach out to us.</p>
            <p class="eb-tpl-sign">
              Warm regards,<br>
              TYNG &amp; CO<br>
              Email: management@tyng-ca.com.my
            </p>
          </div>

        </div>

        <div class="tp-notify-foot">
          <button class="tp-notify-send-btn eb-confirm-cancel" @click="showTemplatePreview = false">
            <fa-icon :icon="['fas', 'times']" />
            Close
          </button>
          <button class="tp-notify-send-btn eb-confirm-proceed" @click="showTemplatePreview = false; showBlastConfirm = true">
            <fa-icon :icon="['fas', 'paper-plane']" />
            Proceed to Blast
          </button>
        </div>

      </div>
    </div>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AppButton from '@/components/shared/AppButton.vue'
import AppBadge from '@/components/shared/AppBadge.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'

export default {
  name: 'admin-email-blast-monthly',
  mixins: [adminSessionMixin],
  components: { AdminHeader, ConfirmModal, SessionModal, AppButton, AppBadge },
  data () {
    return {
      loading: false,
      serverTime: { month: null, year: null, month_name: null, date: null },
      tableRows: [],
      blasting: { active: false, done: false },
      showBlastConfirm: false,
      showTemplatePreview: false,
      summary: {
        show: false,
        sent: 0,
        partial: 0,
        skippedNoDeadlines: 0,
        skippedNoRecipient: 0,
        failed: 0,
        errors: []
      }
    }
  },
  computed: {
    eligibleCount () {
      return this.tableRows.filter(r => r.status === 'pending').length
    },
    noDeadlinesCount () {
      return this.tableRows.filter(r => r.status === 'no-deadlines').length
    },
    noRecipientCount () {
      return this.tableRows.filter(r => r.status === 'no-recipient').length
    },
    sentCount () {
      return this.tableRows.filter(r => r.status === 'sent').length
    },
    skippedCount () {
      return this.tableRows.filter(r => ['no-deadlines', 'no-recipient', 'skipped'].includes(r.status)).length
    },
    failedCount () {
      return this.tableRows.filter(r => r.status === 'failed').length
    },
    // First pending row used for the template preview modal
    previewRow () {
      return this.tableRows.find(r => r.status === 'pending') || this.tableRows[0] || null
    }
  },
  watch: {
    'blasting.active' (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    },
    'summary.show' (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    },
    showBlastConfirm (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    },
    showTemplatePreview (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    }
  },
  mounted () {
    this.startSessionTimer()
    this.fetchData()
    this._beforeUnload = (e) => {
      if (this.blasting.active) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._beforeUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this._beforeUnload)
    if (this.blasting.active || this.summary.show) unlockBodyScroll()
  },
  methods: {
    async fetchData () {
      this.loading = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/email-blast-clients.php')
        if (data === null) { this.pendingAdminRetry = () => this.fetchData(); return }
        if (data && data.success) {
          this.serverTime = data.server_time || {}
          this.buildRows(data.data || [])
        }
      } catch (err) {
        console.error('Failed to load email blast clients:', err)
      } finally {
        this.loading = false
      }
    },

    buildRows (clients) {
      this.tableRows = clients.map(c => {
        const extract = (role) => {
          const emails = []
          const missing = []
          for (const card of (c.contacts || [])) {
            if (!Array.isArray(card.roles) || !card.roles.includes(role)) continue
            const email = (card.email || '').trim()
            if (email) {
              emails.push(email)
            } else {
              missing.push(card.name || 'Unknown')
            }
          }
          return { emails, missing }
        }

        const to = extract('to')
        const cc = extract('cc')
        const bcc = extract('bcc')

        // Mirror PHP: $toList[0]['name'] ?: 'Valued Client'
        const firstToCard = (c.contacts || []).find(card =>
          Array.isArray(card.roles) && card.roles.includes('to') && (card.email || '').trim()
        )
        const toName = (firstToCard?.name || '').trim() || 'Valued Client'

        const hasDeadlines = (c.deadlines || []).length > 0
        const hasRecipient = to.emails.length > 0

        let initStatus = 'pending'
        if (!hasDeadlines) initStatus = 'no-deadlines'
        else if (!hasRecipient) initStatus = 'no-recipient'

        return {
          id: c.id,
          client: c.client,
          initial: c.initial,
          client_type: c.client_type,
          services: c.services || [],
          to,
          cc,
          bcc,
          toName,
          deadlines: c.deadlines || [],
          status: initStatus,
          error: null,
          failed_recipients: []
        }
      })
    },

    openTemplatePreview () {
      this.showBlastConfirm = false // close confirm if open
      this.showTemplatePreview = true
    },

    // Format deadline date as DD/MM/YYYY — mirrors PHP sprintf('%02d/%02d/%04d', $day, $month, $year)
    formatDeadlineDate (dl) {
      if (!dl.date) return '—'
      const day = String(dl.date.day || 0).padStart(2, '0')
      const month = String(dl.date.month || this.serverTime.month || 0).padStart(2, '0')
      const year = String(this.serverTime.year || '')
      return `${day}/${month}/${year}`
    },

    confirmBlast () {
      this.showBlastConfirm = false
      this.runBlast()
    },

    async runBlast () {
      if (this.blasting.active || this.blasting.done) return
      this.blasting.active = true

      const { month, year } = this.serverTime
      const eligible = this.tableRows.filter(r => r.status === 'pending')

      for (let i = 0; i < eligible.length; i++) {
        const row = eligible[i]
        this.$set(row, 'status', 'sending')
        try {
          const data = await this.adminFetch('/api/admin/tasks/send-monthly-deadline-blast.php', {
            method: 'POST',
            body: JSON.stringify({ client_id: row.id, month, year, auto_bcc: ['support@tyng-ca.com.my'] })
          })
          if (data === null) {
            this.$set(row, 'status', 'failed')
            this.$set(row, 'error', 'Session expired')
            break
          }
          const status = data.status || (data.success ? 'sent' : 'failed')
          this.$set(row, 'status', status)
          if (data.failed_recipients && data.failed_recipients.length) {
            this.$set(row, 'failed_recipients', data.failed_recipients)
          }
          if (data.reason) this.$set(row, 'error', data.reason)
          if (data.error) this.$set(row, 'error', data.error)
        } catch (err) {
          this.$set(row, 'status', 'failed')
          this.$set(row, 'error', err.message)
        }

        // 2–3 second delay between sends to avoid spam detection
        if (i < eligible.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 1000))
        }
      }

      this.blasting.active = false
      this.blasting.done = true
      this.openSummary()
    },

    openSummary () {
      const rows = this.tableRows
      this.summary = {
        show: true,
        sent: rows.filter(r => r.status === 'sent').length,
        partial: rows.filter(r => r.status === 'partial').length,
        skippedNoDeadlines: rows.filter(r => r.status === 'no-deadlines').length,
        skippedNoRecipient: rows.filter(r => ['no-recipient', 'skipped'].includes(r.status)).length,
        failed: rows.filter(r => r.status === 'failed').length,
        errors: rows.filter(r => r.status === 'failed').map(r => ({ client: r.client, error: r.error }))
      }
    },

    blastBadgeClass (row) {
      if (row.status === 'no-deadlines' || row.status === 'no-recipient') return 'tp-notify-badge--skipped'
      return 'tp-notify-badge--' + row.status
    },

    blastStatusLabel (row) {
      if (row.status === 'partial') {
        const total = row.to.emails.length + row.cc.emails.length + row.bcc.emails.length
        const failedCount = (row.failed_recipients || []).length
        return `Partial — ${total - failedCount} sent, ${failedCount} failed`
      }
      const map = {
        pending: 'Pending',
        sending: 'Sending…',
        sent: 'Sent',
        failed: 'Failed',
        skipped: row.error || 'Skipped',
        'no-deadlines': 'No Deadlines',
        'no-recipient': 'No Recipient'
      }
      return map[row.status] || row.status
    },

    emailClass (row, email) {
      const done = ['sent', 'partial', 'failed'].includes(row.status)
      if (!done) return ''
      if ((row.failed_recipients || []).includes(email)) return 'tp-notify-email--failed'
      if (row.status !== 'failed') return 'tp-notify-email--success'
      return ''
    },

    formatClientType (val) {
      const map = {
        sdn_bhd: 'Sdn Bhd',
        llp: 'LLP',
        sole_prop: 'Sole Prop',
        partnership: 'Partnership',
        salaried_person: 'Salaried'
      }
      return map[val] || val || '—'
    }
  }
}
</script>

<style scoped>
/* ── Blast confirm modal ─────────────────────────────────────────── */
.eb-confirm-modal {
  max-width: 560px;
  width: 90vw;
}

.eb-confirm-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
}

/* Send / skip summary row */
.eb-confirm-stats {
  display: flex;
  gap: 12px;
}

.eb-confirm-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
}

.eb-confirm-stat--send {
  background: #eff6ff;
  color: #1d4ed8;
}

.eb-confirm-stat--skip {
  background: #f8fafc;
  color: #64748b;
}

.eb-confirm-stat-val {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.eb-confirm-stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Preview label */
.eb-confirm-preview-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: -8px;
}

/* Email preview card */
.eb-email-preview {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
  color: #334155;
}

.eb-email-preview-meta {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.eb-email-preview-field {
  display: flex;
  gap: 8px;
  align-items: baseline;
}

.eb-email-preview-key {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  min-width: 52px;
}

.eb-email-preview-muted {
  color: #94a3b8;
  font-style: italic;
}

.eb-email-preview-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  line-height: 1.6;
}

.eb-email-preview-body p { margin: 0; }

/* Placeholder deadline rows */
.eb-email-preview-deadlines {
  background: #f8fafc;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 2px 0;
}

.eb-email-preview-dl-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.eb-email-preview-dl-row--dim {
  opacity: 0.45;
}

.eb-email-preview-dl-day {
  color: #2563eb;
  font-weight: 600;
}

.eb-email-preview-sign {
  font-size: 12px;
  color: #64748b;
}

/* Confirm note text */
.eb-confirm-note {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* Footer buttons */
.eb-confirm-cancel {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.eb-confirm-cancel:hover {
  background: #e2e8f0;
}

.eb-confirm-preview-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.eb-confirm-preview-btn:hover {
  background: #e2e8f0;
}

.eb-confirm-proceed {
  background: #2563eb;
  color: #fff;
  border: none;
}

.eb-confirm-proceed:hover {
  background: #1d4ed8;
}

/* ── Email Template Preview Modal ───────────────────────────────── */
.eb-tpl-modal {
  max-width: 620px;
  width: 92vw;
}

.eb-tpl-body {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
}

/* Envelope header block */
.eb-tpl-headers {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.eb-tpl-header-row {
  display: flex;
  gap: 10px;
  font-size: 13px;
  align-items: baseline;
}

.eb-tpl-hkey {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  min-width: 56px;
  flex-shrink: 0;
}

.eb-tpl-hval {
  color: #334155;
  word-break: break-all;
}

.eb-tpl-muted {
  color: #94a3b8;
}

/* Plain-text email body */
.eb-tpl-email-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13.5px;
  line-height: 1.65;
  color: #1e293b;
  font-family: 'Open Sans', sans-serif;
}

.eb-tpl-email-body p { margin: 0; }

/* Deadline list */
.eb-tpl-deadlines {
  background: #f8fafc;
  border-left: 3px solid #2563eb;
  border-radius: 4px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 2px 0;
}

.eb-tpl-dl-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
}

.eb-tpl-dl-bullet {
  color: #2563eb;
  font-size: 16px;
  line-height: 1;
}

.eb-tpl-dl-name {
  flex: 1;
  color: #1e293b;
}

.eb-tpl-dl-due {
  font-weight: 600;
  color: #2563eb;
  white-space: nowrap;
}

/* Sign-off block */
.eb-tpl-sign {
  color: #475569;
  border-top: 1px solid #e2e8f0;
  padding-top: 10px;
  margin-top: 4px !important;
  font-size: 13px;
  line-height: 1.7;
}
</style>
